import * as React from "react";
import { useEffect } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { CarouselContainer } from "@similarweb/ui-components";
import {
    CarouselButtonIconContainer,
    CarouselButtonLeft,
    CarouselButtonRight,
    CarouselContainerWrapper,
} from "./Carousel.styles";
import { allTrackers } from "app/services/track/track";

interface ICarouselProps {
    margin: number;
    offset?: number;
    children: React.ReactNode[];
    forceHover?: boolean;
    isTable?: boolean;
    initialSelectedItem?: number;
    onSelectedItemChange?: (setSelectedItem: number) => void;
    carouselTracking?: (direction: EDirection) => void;
    goToEnd?: boolean;
    selectedIndex?: number;
}

enum EDirection {
    PREV = "prev",
    NEXT = "next",
}

export const Carousel: React.FunctionComponent<ICarouselProps> = ({
    margin,
    offset = 0,
    forceHover,
    children,
    isTable,
    onSelectedItemChange,
    initialSelectedItem = 0,
    carouselTracking,
    goToEnd = false,
    selectedIndex,
}) => {
    const [selectedItem, setSelectedItem] = React.useState<number>(initialSelectedItem);
    const [totalSlidableItems, setTotalSlidableItems] = React.useState<number>(0);

    const changeSelectedItem = (direction: EDirection, value: number) => {
        setSelectedItem(value);
        onSelectedItemChange && onSelectedItemChange(value);
        carouselTracking
            ? carouselTracking(direction)
            : allTrackers.dispatchTrackEvent("Button", "click", `carousel/${direction || ""}`);
    };

    const next = () => {
        if (totalSlidableItems > 0 && selectedItem < totalSlidableItems) {
            const nextSelectedItem = goToEnd ? totalSlidableItems : selectedItem + 1;
            changeSelectedItem(EDirection.NEXT, nextSelectedItem);
        }
    };

    const prev = () => {
        if (selectedItem > 0) {
            const prevSelectedItem = goToEnd ? 0 : selectedItem - 1;
            changeSelectedItem(EDirection.PREV, prevSelectedItem);
        }
    };

    const getTotalSlidableItems = (totalSlidableItems: number) => {
        setTotalSlidableItems(totalSlidableItems);
        !totalSlidableItems && setSelectedItem(0);
    };

    const isRightVisible = totalSlidableItems > 0 && selectedItem < totalSlidableItems;
    const isLeftVisible = selectedItem > 0;
    const totalItems = React.Children.count(children);
    const rightItemIndex = totalItems - totalSlidableItems + selectedItem - 1;

    useEffect(() => {
        if (selectedIndex !== undefined) {
            setSelectedItem((prev) => {
                if (prev === selectedIndex) {
                    return prev;
                }

                if (prev < selectedIndex && isRightVisible && selectedIndex > rightItemIndex) {
                    return prev + 1;
                }

                if (prev > selectedIndex && isLeftVisible) {
                    return selectedIndex;
                }

                return prev;
            });

            onSelectedItemChange?.(selectedIndex);
        }
    }, [selectedIndex, onSelectedItemChange, isRightVisible, isLeftVisible]);

    return (
        <CarouselContainerWrapper
            rightVisible={isRightVisible}
            leftVisible={isLeftVisible}
            forceHover={forceHover}
        >
            <CarouselContainer
                selectedItem={selectedItem}
                margin={margin}
                offset={offset}
                getTotalSlidableItems={getTotalSlidableItems}
            >
                {children}
            </CarouselContainer>
            <CarouselButtonLeft
                isTable={isTable}
                onClick={prev}
            >
                <CarouselButtonIconContainer>
                    <SWReactIcons iconName="arrow-left" />
                </CarouselButtonIconContainer>
            </CarouselButtonLeft>
            <CarouselButtonRight
                isTable={isTable}
                onClick={next}
            >
                <CarouselButtonIconContainer>
                    <SWReactIcons iconName="arrow-right" />
                </CarouselButtonIconContainer>
            </CarouselButtonRight>
        </CarouselContainerWrapper>
    );
};
